import { context, propagation, Span, trace } from '@opentelemetry/api';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { keyBy } from 'lodash';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { v4 as uuid } from 'uuid';
import { store } from '../../common/redux/configure-store';
import {
  pauseWithSpinner,
  resumeWithSpinner,
  startRequestSpinner,
  stopRequestSpinner,
} from '../components/with-spinner/with-spinner';
import ElectronHelper from '../electron/ElectronHelper';
import { BrmActionType } from '../enumerations/brm-action-type';
import { LocalStorage } from '../helpers/local-storage';
import { LOCAL_CULTURE } from '../helpers/localized-description-helper';
import XLRLogger from '../helpers/logger';
import { SessionStorage } from '../helpers/session-storage';
import { createSpan, endSpan } from '../helpers/tracing-helper';
import { EmeraldRequestParams } from '../interfaces/requests/EmeraldRequestParams';
import { BRMInputActions } from '../redux/reducers/brm-input-reducer';
import { axiosRequest } from './axios-request';
import { dispatchActions, dispatchNotification, IBRMInput, parseBRMActions } from './brm/brm-utils';
import { EmeraldError, parseClientEmeraldErrors } from './emerald-error';
import { parseCommunicationEmeraldErrors } from './parse-communication-errors';
import { dispatchReturnPolicy, parseReturnPolicy } from './return-policy/return-policy-utils';

const source = 'emerald-request.ts';

const emeraldRequestParamsDefault: EmeraldRequestParams = {
  addPOSMetaData: true,
  config: {},
  showLoadingComponent: false,
  showLoggingInfo: true,
};

export const emeraldRequest = async <T>(
  // TODO remove showLoadingComponent from request params
  // use withSpinner when a request needs spinner
  emeraldRequestParams: EmeraldRequestParams = emeraldRequestParamsDefault,
): Promise<T> => {
  const { addPOSMetaData, config, showLoadingComponent, showLoggingInfo } = emeraldRequestParams;
  startRequestSpinner(showLoadingComponent);
  const CREATE_CART_ROUTE = '/transaction-management/transactions';
  const baseURL = await ElectronHelper.getBaseURL();

  const storeState = store.getState();

  if (addPOSMetaData) {
    const metaData = {
      token:
        emeraldRequestParams.sessionId ?? storeState.user.sessionId ?? SessionStorage.sessionId(),
      'transaction-id': storeState.transaction.transactionId,
      'till-id': LocalStorage.tillId(),
      'touchpoint-group-id': LocalStorage.touchpointGroupId(),
      'store-id': LocalStorage.storeId(),
      'touchpoint-application':
        storeState.touchPoint.touchPointConfiguration.touchPointApplicationId,
      locale: LOCAL_CULTURE,
      'client-name': process.env.REACT_APP_NAME,
      'client-version': process.env.REACT_APP_VERSION,
    };
    const { auditUserHeader } = getCurrentXLRConfig();
    if (config.url === CREATE_CART_ROUTE && auditUserHeader && config.method === 'POST') {
      const { EDGE_IAM } = getMetaData().xlrConfig;
      if (EDGE_IAM) {
        metaData['audit-user-name'] = `Bearer ${storeState.user.sessionId}`;
      } else {
        metaData['audit-user-name'] = store.getState().user.userName;
      }
    }
    config.headers = { ...config.headers, ...metaData };
  }
  // The proxy defined in package.json should preappend correct domain to url
  // The following line simply prepends `/api` to an existing `/some-endpoint` URL
  config.url = `${baseURL}/api${config.url}`;

  config.headers = {
    ...config.headers,
    'message-id': uuid(),
  };
  let span: Span;
  const spanOptions = emeraldRequestParams.spanOptions;

  if (!spanOptions?.skipSpan) {
    span = createSpan(
      `[Emerald Request] ${spanOptions?.name ?? config.url}`,
      spanOptions?.parentContext,
      true,
    );
    span.setAttributes({
      'http-action': config.method,
      'message-id': config.headers['message-id'],
    });
    //injecting trace/span context into request headers
    propagation.inject(trace.setSpanContext(context.active(), span.spanContext()), config.headers);
  }

  const xlrConfig = getCurrentXLRConfig();
  if (xlrConfig.mergeBrmInputs) {
    getBRMInputsStateIntoRequest(config);
  }

  const message = 'BFF Request';
  if (showLoggingInfo) {
    XLRLogger.info({ message, source, data: config });
  } else {
    XLRLogger.info({ message, source });
  }

  return request<T>(config, 0, showLoggingInfo, showLoadingComponent)
    .then(response => {
      stopRequestSpinner(!!showLoadingComponent, false);
      return response;
    })
    .catch(err => {
      stopRequestSpinner(!!showLoadingComponent, true);
      throw err;
    })
    .finally(() => {
      if (span) {
        endSpan(span);
      }
    });
};

const request = async <T>(
  config: AxiosRequestConfig,
  depth: number,
  showLoggingInfo: boolean = true,
  showSpinner?: boolean,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    axiosRequest(config)
      .then(async response => {
        if (showLoggingInfo) {
          logSuccessResponse(response);
        } else {
          XLRLogger.info({ message: 'BFF response', source });
        }

        let emeraldData = response.data;
        const brmNotifications = emeraldData.brmNotifications;

        if (brmNotifications) {
          for await (const notification of brmNotifications) {
            await dispatchNotification(notification);
          }
          // If there are brmNotifications, the returned data models will be changed to
          // { response: data, brmNotifications: notifications[] }
          emeraldData = emeraldData.response;
        }

        resolve(emeraldData);
      })
      .catch(async error => {
        const response = error.response;
        if (showLoggingInfo) {
          logUnsuccessfulResponse(response);
        } else {
          XLRLogger.info({ message: 'BFF response', source });
        }

        const communicationError = parseCommunicationEmeraldErrors(error);
        if (communicationError) {
          reject(communicationError);
          return;
        }

        const emeraldError: EmeraldError = response.data;
        config.data = config.data ?? {};

        const brmActions = parseBRMActions(emeraldError);

        if (brmActions) {
          return dispatchActions(brmActions, depth)
            .then(inputs => {
              const xlrConfig = getCurrentXLRConfig();
              if (!xlrConfig.mergeBrmInputs) {
                config.data.brmInputs = inputs;
              } else {
                buildInputKey(inputs);
                config.data.brmInputs = mergeInputs('inputKey', config.data.brmInputs, inputs);
                saveBRMInputsIntoState(config);
              }
              resumeWithSpinner();
              return request<T>(config, depth + 1, showLoggingInfo);
            })
            .then(resolve)
            .catch(reject)
            .finally(pauseWithSpinner);
        }

        const returnPolicy = parseReturnPolicy(emeraldError);
        if (returnPolicy) {
          return dispatchReturnPolicy(returnPolicy, depth)
            .then(input => {
              config.data.returnPolicy = mergeInputs('type', config.data.returnPolicy, [input]);
              resumeWithSpinner();
              return request<T>(config, depth + 1, showLoggingInfo, showSpinner);
            })
            .then(resolve)
            .catch(reject)
            .finally(pauseWithSpinner);
        }
        const clientError = parseClientEmeraldErrors(emeraldError);
        reject(clientError);
      });
  });
};

export const mergeInputs = <T>(key: string, originalInputs?: T[], newInputs?: T[]): T[] => {
  const originalObj = keyBy(originalInputs, key);
  const newObj = keyBy(newInputs, key);
  const inputObj = { ...originalObj, ...newObj };
  const inputs = Object.entries(inputObj).map(([, input]) => input);
  return inputs;
};

const logSuccessResponse = (response: AxiosResponse): void => {
  // Todo: Add client logging service: https://jira.ncr.com/browse/ATLE-1047?filter=-1
  logResponse(response);
};

const logUnsuccessfulResponse = (response: AxiosResponse): void => {
  logResponse(response);
};

const logResponse = (response?: AxiosResponse): void => {
  if (response) {
    XLRLogger.info({ message: 'BFF response', source, data: response });
  }
};

const getBRMInputsState = (): IBRMInput[] => {
  return store.getState().brmInput.brmInputs;
};

const saveBRMInputsIntoState = (config): void => {
  store.dispatch(BRMInputActions.setBRMInputState(config.data.brmInputs));
};

const buildInputKey = (inputs: IBRMInput[]): void => {
  const inputKey = `${inputs[0]?.ruleId}_${inputs[0]?.type}`;
  inputs[0].inputKey = inputKey;
};

const getBRMInputsStateIntoRequest = (config: AxiosRequestConfig): void => {
  let brmInputs = getBRMInputsState();
  /*
  filter out rules that shouldn't added to request since we need the user input
  each time request is being sent, like approvals.
  */
  brmInputs = filterInputRules(brmInputs);

  if (config.data && brmInputs && brmInputs.length > 0) {
    config.data.brmInputs = brmInputs;
  }
};

const filterInputRules = (inputs: IBRMInput[]): IBRMInput[] => {
  return inputs.filter(i => ![BrmActionType.ApprovalAction].includes(i.type));
};
